import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';
import { formatPrice, calculateWalletBonus, WalletBonusTier } from '../../utils/bookingUtils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bonusTiers: WalletBonusTier[];
}

// Preset top-up amounts
const PRESET_AMOUNTS = [500, 1000, 2000, 3000, 4000, 5000];

// Inner payment form component - content only (buttons rendered separately)
const TopUpPaymentFormContent: React.FC<{
  amount: number;
  bonus: { bonusPercent: number; bonusAmount: number; totalCredit: number };
  error: string | null;
}> = ({ amount, bonus, error }) => {
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between mb-2">
          <span className="text-gray-700">Top-up Amount:</span>
          <span className="font-medium">${formatPrice(amount)}</span>
        </div>
        {bonus.bonusAmount > 0 && (
          <div className="flex justify-between mb-2 text-green-600">
            <span>Bonus:</span>
            <span>+${formatPrice(bonus.bonusAmount)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-blue-200">
          <span className="font-medium text-gray-900">Total Credit:</span>
          <span className="font-bold text-blue-600">${formatPrice(bonus.totalCredit)}</span>
        </div>
      </div>

      {/* Payment Element */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Details
        </label>
        <PaymentElement />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
          {error}
        </div>
      )}
    </div>
  );
};

// Inner payment form buttons - rendered in fixed footer
const TopUpPaymentFormButtons: React.FC<{
  amount: number;
  isProcessing: boolean;
  onBack: () => void;
}> = ({ amount, isProcessing, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();

  return (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={onBack}
        disabled={isProcessing}
        className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        Back
      </button>
      <button
        type="submit"
        disabled={!stripe || !elements || isProcessing}
        className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
      >
        {isProcessing ? (
          <>
            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
            Processing...
          </>
        ) : (
          `Pay $${formatPrice(amount)}`
        )}
      </button>
    </div>
  );
};

// Wrapper component that handles form submission
const TopUpPaymentForm: React.FC<{
  amount: number;
  bonus: { bonusPercent: number; bonusAmount: number; totalCredit: number };
  onSuccess: () => void;
  onBack: () => void;
}> = ({ amount, bonus, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm payment
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Confirm the top-up on the backend
        const { data: { session } } = await supabase.auth.getSession();

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CONFIRM_WALLET_TOPUP),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              payment_intent_id: paymentIntent.id,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to confirm top-up');
        }

        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        <TopUpPaymentFormContent amount={amount} bonus={bonus} error={error} />
      </div>

      {/* Fixed footer with buttons - includes safe area padding */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        <TopUpPaymentFormButtons amount={amount} isProcessing={isProcessing} onBack={onBack} />
      </div>
    </form>
  );
};

const TopUpModal: React.FC<TopUpModalProps> = ({ isOpen, onClose, onSuccess, bonusTiers }) => {
  const [step, setStep] = useState<'amount' | 'payment' | 'success'>('amount');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topUpResult, setTopUpResult] = useState<{
    topupAmount: number;
    bonusAmount: number;
    totalCredit: number;
    newBalance: number;
  } | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('amount');
      setSelectedAmount(null);
      setClientSecret(null);
      setError(null);
      setTopUpResult(null);
    }
  }, [isOpen]);

  const getFinalAmount = (): number => {
    return selectedAmount || 0;
  };

  const amount = getFinalAmount();
  const bonus = calculateWalletBonus(amount, bonusTiers);

  const handleContinueToPayment = async () => {
    if (amount <= 0) {
      setError('Please select a top-up amount');
      return;
    }

    setIsCreatingIntent(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Check if user is authenticated
      if (!session?.access_token) {
        throw new Error('Please log in to add funds to your wallet');
      }

      const response = await fetch(
        edgeFunctionUrl(EdgeFunctions.CREATE_WALLET_TOPUP),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ amount }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create top-up');
      }

      const data = await response.json();
      setClientSecret(data.clientSecret);
      setStep('payment');
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = () => {
    setTopUpResult({
      topupAmount: amount,
      bonusAmount: bonus.bonusAmount,
      totalCredit: bonus.totalCredit,
      newBalance: 0, // Will be updated when modal closes
    });
    setStep('success');
  };

  const handleClose = () => {
    if (step === 'success') {
      onSuccess();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] sm:max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'success' ? 'Top-Up Successful!' : 'Add Funds to Wallet'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step 1: Select Amount - with scrollable content and fixed footer */}
        {step === 'amount' && (
          <>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Select an amount to add to your wallet. Get bonus credits on larger top-ups!
                </p>

                {/* Preset Amounts */}
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_AMOUNTS.map((preset) => {
                    const presetBonus = calculateWalletBonus(preset, bonusTiers);
                    return (
                      <button
                        key={preset}
                        onClick={() => {
                          setSelectedAmount(preset);
                        }}
                        className={`p-3 rounded-lg border-2 text-center transition-colors ${
                          selectedAmount === preset
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium text-gray-900">${preset.toLocaleString()}</span>
                        {presetBonus.bonusAmount > 0 && (
                          <span className="block text-xs text-green-600">
                            +${formatPrice(presetBonus.bonusAmount)} bonus
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs text-gray-500">
                  Wallet top-up balance is non-refundable and cannot be redeemed for cash.
                </p>

                {/* Preview */}
                {amount > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">You pay:</span>
                      <span className="font-medium">${formatPrice(amount)}</span>
                    </div>
                    {bonus.bonusAmount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Bonus:</span>
                        <span>+${formatPrice(bonus.bonusAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-200 mt-2">
                      <span>You receive:</span>
                      <span className="text-blue-600">${formatPrice(bonus.totalCredit)}</span>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Footer with safe area padding */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={handleContinueToPayment}
                disabled={amount <= 0 || isCreatingIntent}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isCreatingIntent ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Processing...
                  </>
                ) : (
                  'Continue to Payment'
                )}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Payment - wrapped in flex container for proper height */}
        {step === 'payment' && clientSecret && (
          <div className="flex-1 flex flex-col min-h-0 [&>*]:flex-1 [&>*]:flex [&>*]:flex-col [&>*]:min-h-0">
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#2563eb',
                  },
                },
              }}
            >
              <TopUpPaymentForm
                amount={amount}
                bonus={bonus}
                onSuccess={handlePaymentSuccess}
                onBack={() => {
                  setStep('amount');
                  setClientSecret(null);
                }}
              />
            </Elements>
          </div>
        )}

        {/* Step 3: Success - with scrollable content and fixed footer */}
        {step === 'success' && (
          <>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Funds Added Successfully!
                </h3>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800">
                    <span className="font-medium">${formatPrice(bonus.totalCredit)}</span> has been added to your wallet
                  </p>
                  {bonus.bonusAmount > 0 && (
                    <p className="text-sm text-green-600 mt-1">
                      Includes ${formatPrice(bonus.bonusAmount)} bonus!
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Fixed Footer with safe area padding */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TopUpModal;
